'use client';

import Logo from '@/components/shared/misc/Logo';
import { Card, ErrorMessage, LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { sendRegistrationOTP, verifyRegistrationOTP } from '@/lib/api/auth';
import { suppliersApi } from '@/lib/api/suppliers';
import { useRegister } from '@/lib/hooks/useAuthMutations';
import { useActiveCategories } from '@/lib/hooks/useCategories';
import { cssVars } from '@/styles/theme';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Building2,
  CheckCircle,
  Eye,
  EyeOff,
  FileText,
  Lock,
  Mail,
  Phone,
  Send,
  Sparkles,
  User,
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function RegisterForm() {
  const t = useTranslations('auth');
  const locale = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isRTL = locale === 'ar';
  const { setAuthFromResponse } = useAuth();
  const { register, isLoading, error } = useRegister();

  // User type selection (client or supplier) — يُحدد من الرابط ?type=client|supplier إن وُجد
  const [userType, setUserType] = useState<'client' | 'supplier'>(() => {
    const type = searchParams.get('type');
    return type === 'supplier' ? 'supplier' : 'client';
  });

  // Form fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [commercialReg, setCommercialReg] = useState('');
  const [primaryCategoryId, setPrimaryCategoryId] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  const { data: categories } = useActiveCategories();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState('');

  // OTP verification states
  const [otpCode, setOtpCode] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpMethod, setOtpMethod] = useState<'phone' | 'email'>('phone');

  // Cooldown timer effect
  useEffect(() => {
    if (otpCooldown > 0) {
      const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCooldown]);

  // Reset OTP verification when delivery method / contact changes
  useEffect(() => {
    if (isOtpSent || isOtpVerified) {
      setIsOtpSent(false);
      setIsOtpVerified(false);
      setOtpCode('');
    }
  }, [otpMethod, phoneNumber, email]);

  // Send OTP via selected method (WhatsApp phone / Email)
  const handleSendOtp = async () => {
    if (otpMethod === 'phone') {
      if (!phoneNumber) {
        setValidationError(t('register.otp.phoneRequired'));
        return;
      }
    } else {
      if (!email) {
        setValidationError(t('register.otp.emailRequired'));
        return;
      }
    }

    setIsSendingOtp(true);
    setValidationError('');

    try {
      const result =
        otpMethod === 'email'
          ? await sendRegistrationOTP({ method: 'email', email })
          : await sendRegistrationOTP({ method: 'phone', phone: phoneNumber });
      setIsOtpSent(true);
      setOtpCooldown(60); // 60 seconds cooldown

      // In development mode, show the OTP code in the toast for testing
      if (result.otpCode) {
        toast.success(
          `${t(otpMethod === 'email' ? 'register.otp.codeSentEmail' : 'register.otp.codeSentWhatsApp')} (Dev: ${result.otpCode})`
        );
        console.log('🔐 Development OTP Code:', result.otpCode);
      } else {
        toast.success(
          t(otpMethod === 'email' ? 'register.otp.codeSentEmail' : 'register.otp.codeSentWhatsApp')
        );
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      toast.error(errorMessage || t('register.otp.sendFailed'));
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP code
  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) {
      setValidationError(t('register.otp.codeRequired'));
      return;
    }
    if (otpMethod === 'phone' && !phoneNumber) {
      setValidationError(t('register.otp.phoneRequired'));
      return;
    }
    if (otpMethod === 'email' && !email) {
      setValidationError(t('register.otp.emailRequired'));
      return;
    }

    setIsVerifyingOtp(true);
    setValidationError('');

    try {
      const result =
        otpMethod === 'email'
          ? await verifyRegistrationOTP({ method: 'email', email, otpCode })
          : await verifyRegistrationOTP({ method: 'phone', phone: phoneNumber, otpCode });
      if (result.verified) {
        setIsOtpVerified(true);
        toast.success(
          t(otpMethod === 'email' ? 'register.otp.verifiedEmail' : 'register.otp.verifiedPhone')
        );
      } else {
        setValidationError(t('register.otp.invalidCode'));
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setValidationError(errorMessage || t('register.otp.verifyFailed'));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    // Validation
    if (password !== confirmPassword) {
      setValidationError(t('common.passwordsNotMatch'));
      return;
    }

    if (!agreeTerms) {
      setValidationError(t('common.agreeTermsError'));
      return;
    }

    if (userType === 'supplier' && !primaryCategoryId) {
      setValidationError(t('register.specializationRequired'));
      return;
    }

    // OTP verification is required
    if (!isOtpVerified) {
      setValidationError(
        t(
          otpMethod === 'email'
            ? 'register.otp.verificationRequiredEmail'
            : 'register.otp.verificationRequiredPhone'
        )
      );
      return;
    }

    try {
      const response = await register({
        fullName,
        email,
        password,
        userType,
        phoneNumber,
        companyName: userType === 'supplier' ? companyName : undefined,
        commercialReg: userType === 'supplier' ? commercialReg : undefined,
        verifiedVia: otpMethod, // Pass verification method so backend sets IsEmailVerified/IsPhoneVerified
      });

      // Update auth state using the response we already have
      const authSet = setAuthFromResponse(response);
      if (!authSet) {
        console.warn('Auth state was not set from registration response');
      }

      // Create minimal supplier profile for suppliers (token is now in localStorage)
      if (userType === 'supplier' && primaryCategoryId) {
        try {
          await suppliersApi.createProfile({
            serviceDescription:
              'This is a temporary profile created during registration. Please complete your service description and other details through the profile setup page. You can return and complete your profile at any time to activate all features and appear in search results for clients.',
            categories: [{ categoryId: primaryCategoryId, isPrimary: true }],
          });
        } catch (profileErr) {
          // Log but don't block - user is registered, they can complete profile later
          console.warn('Supplier profile creation failed:', profileErr);
          toast.info(
            t('register.profileCreationDeferred') || 'تم التسجيل. يرجى إكمال الملف الشخصي من لوحة التحكم.'
          );
        }
      }

      // Show welcome message based on user type
      const userTypeAfterRegister = response?.user?.userType || userType;
      const welcomeMessageKey =
        userTypeAfterRegister === 'client'
          ? 'register.welcomeMessages.client'
          : 'register.welcomeMessages.supplier';
      toast.success(t(welcomeMessageKey));

      // Registration completed - go to dashboard (no extra email verification step)
      router.replace(`/${locale}/dashboard`);
      router.refresh();
    } catch (err) {
      // Error handled by hook
    }
  };

  return (
    <Card className="w-full max-w-md p-4 sm:p-6 md:p-8">
      {/* Header */}
      <div className="mb-6 text-center sm:mb-8">
        {/* Brand Logo */}
        <div className="mb-4 flex justify-center sm:mb-6">
          <Logo width={64} height={64} showText={false} withName={true} />
        </div>

        <div
          className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 sm:mb-4 sm:px-4 sm:py-2"
          style={{
            backgroundColor: `color-mix(in srgb, ${cssVars.accent.primary} 10%, transparent)`,
            border: `1px solid ${cssVars.accent.primary}30`,
          }}
        >
          <Sparkles
            className="h-3.5 w-3.5 sm:h-4 sm:w-4"
            style={{ color: cssVars.accent.primary }}
          />
          <span className="text-sm font-bold sm:text-base" style={{ color: cssVars.accent.primary }}>
            {t('register.badge')}
          </span>
        </div>

        <h1
          className="mb-2 text-2xl font-bold sm:text-3xl md:text-4xl"
          style={{ color: cssVars.secondary.DEFAULT }}
        >
          {t('register.title')}
        </h1>
        <p className="text-sm sm:text-base" style={{ color: cssVars.neutral.textSecondary }}>
          {t('register.subtitle')}
        </p>
      </div>

      {/* Error Message */}
      {(error || validationError) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <ErrorMessage
            error={error?.message || validationError || (error ? String(error) : '')}
            variant="inline"
            onDismiss={() => {
              setValidationError('');
            }}
          />
        </motion.div>
      )}

      {/* User Type Toggle */}
      <div className="mb-6 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => {
            setUserType('client');
            setPrimaryCategoryId('');
            setValidationError('');
          }}
          className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 text-xs font-semibold transition-all sm:px-4 sm:py-3 sm:text-sm ${
            userType === 'client'
              ? 'border-transparent'
              : 'border-transparent opacity-70 hover:opacity-100'
          }`}
          style={{
            backgroundColor:
              userType === 'client' ? cssVars.primary.DEFAULT : cssVars.neutral.surface,
            color: userType === 'client' ? cssVars.neutral.white : cssVars.secondary.DEFAULT,
          }}
        >
          <User className="h-4 w-4 sm:h-5 sm:w-5" />
          {t('register.userType.client.title')}
        </button>
        <button
          type="button"
          onClick={() => {
            setUserType('supplier');
            setValidationError('');
          }}
          className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 text-xs font-semibold transition-all sm:px-4 sm:py-3 sm:text-sm ${
            userType === 'supplier'
              ? 'border-transparent'
              : 'border-transparent opacity-70 hover:opacity-100'
          }`}
          style={{
            backgroundColor:
              userType === 'supplier' ? cssVars.primary.DEFAULT : cssVars.neutral.surface,
            color: userType === 'supplier' ? cssVars.neutral.white : cssVars.secondary.DEFAULT,
          }}
        >
          <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
          {t('register.userType.supplier.title')}
        </button>
      </div>

      {/* Quick Fill Buttons (Development Only) */}
      {typeof window !== 'undefined' && process.env.NODE_ENV === 'development' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-lg border-2 p-4"
          style={{
            backgroundColor: `color-mix(in srgb, ${cssVars.primary.DEFAULT} 5%, transparent)`,
            borderColor: cssVars.primary.DEFAULT + '40',
          }}
        >
          <div className="mb-3 text-center">
            <span
              className="text-sm font-bold sm:text-base"
              style={{ color: cssVars.primary.DEFAULT }}
            >
              {t('devMode.defaultData')}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              {
                label: t('devMode.quickFill.users.hamadClient.label'),
                fullName: t('devMode.quickFill.users.hamadClient.fullName'),
                email: t('devMode.quickFill.users.hamadClient.email'),
                password: '11223344',
                userType: 'client' as const,
                phoneNumber: t('devMode.quickFill.users.hamadClient.phoneNumber'),
                companyName: '',
                commercialReg: '',
                icon: User,
              },
              {
                label: t('devMode.quickFill.users.hamadSupplier.label'),
                fullName: t('devMode.quickFill.users.hamadSupplier.fullName'),
                email: t('devMode.quickFill.users.hamadSupplier.email'),
                password: '11223344',
                userType: 'supplier' as const,
                phoneNumber: t('devMode.quickFill.users.hamadSupplier.phoneNumber'),
                companyName: t('devMode.quickFill.users.hamadSupplier.companyName'),
                commercialReg: '1234567890',
                icon: Building2,
              },
            ].map((data) => {
              const Icon = data.icon;
              const handleQuickFill = () => {
                setFullName(data.fullName);
                setEmail(data.email);
                setPassword(data.password);
                setConfirmPassword(data.password);
                setPhoneNumber(data.phoneNumber);
                setCompanyName(data.companyName);
                setCommercialReg(data.commercialReg);
                setUserType(data.userType);
                setPrimaryCategoryId(data.userType === 'supplier' ? categories?.[0]?.id ?? '' : '');
                setAgreeTerms(true);
                setValidationError('');
              };
              return (
                <motion.button
                  key={data.email}
                  type="button"
                  onClick={handleQuickFill}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex flex-col items-center gap-2 rounded-lg border-2 py-2 text-xs font-bold transition-all"
                  style={{
                    backgroundColor: cssVars.neutral.bg,
                    borderColor: cssVars.primary.DEFAULT + '60',
                    color: cssVars.primary.DEFAULT,
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-center text-xs sm:text-sm">{data.label}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>
        {/* Full Name */}
        <div>
          <label
            className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
            style={{ color: cssVars.secondary.DEFAULT }}
          >
            {t('register.fullName')}
          </label>
          <div className="relative">
            <User
              className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
              style={{ color: cssVars.neutral.textMuted }}
            />
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="w-full rounded-lg border-2 py-2.5 pl-3 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-12 sm:text-base"
              style={{
                backgroundColor: cssVars.neutral.bg,
                color: cssVars.secondary.DEFAULT,
                borderColor: cssVars.neutral.border,
              }}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label
            className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
            style={{ color: cssVars.secondary.DEFAULT }}
          >
            {t('common.email')}
          </label>
          <div className="relative">
            <Mail
              className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
              style={{ color: cssVars.neutral.textMuted }}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              dir="ltr"
              className="w-full rounded-lg border-2 py-2.5 pl-3 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-12 sm:text-base"
              style={{
                backgroundColor: cssVars.neutral.bg,
                color: cssVars.secondary.DEFAULT,
                borderColor: cssVars.neutral.border,
              }}
              placeholder={t('emailPlaceholder')}
            />
          </div>
        </div>

        {/* Phone Number */}
        <div>
          <label
            className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
            style={{ color: cssVars.secondary.DEFAULT }}
          >
            {t('register.phoneNumber')}
          </label>
          <div className="relative">
            <Phone
              className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
              style={{ color: cssVars.neutral.textMuted }}
            />
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              className="w-full rounded-lg border-2 py-2.5 pl-3 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-12 sm:text-base"
              style={{
                backgroundColor: cssVars.neutral.bg,
                color: cssVars.secondary.DEFAULT,
                borderColor: cssVars.neutral.border,
              }}
            />
          </div>
        </div>

        {/* Supplier-specific fields */}
        {userType === 'supplier' && (
          <>
            <div>
              <label
                className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
                style={{ color: cssVars.secondary.DEFAULT }}
              >
                {t('register.companyName')}
              </label>
              <div className="relative">
                <Building2
                  className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
                  style={{ color: cssVars.neutral.textMuted }}
                />
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="w-full rounded-lg border-2 py-2.5 pl-3 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-12 sm:text-base"
                  style={{
                    backgroundColor: cssVars.neutral.bg,
                    color: cssVars.secondary.DEFAULT,
                    borderColor: cssVars.neutral.border,
                  }}
                />
              </div>
            </div>

            <div>
              <label
                className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
                style={{ color: cssVars.secondary.DEFAULT }}
              >
                {t('register.commercialRegistration')}
              </label>
              <div className="relative">
                <FileText
                  className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
                  style={{ color: cssVars.neutral.textMuted }}
                />
                <input
                  type="text"
                  value={commercialReg}
                  onChange={(e) => setCommercialReg(e.target.value)}
                  required
                  className="w-full rounded-lg border-2 py-2.5 pl-3 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-12 sm:text-base"
                  style={{
                    backgroundColor: cssVars.neutral.bg,
                    color: cssVars.secondary.DEFAULT,
                    borderColor: cssVars.neutral.border,
                  }}
                />
              </div>
            </div>

            <div>
              <label
                className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
                style={{ color: cssVars.secondary.DEFAULT }}
              >
                {t('register.specialization')}
              </label>
              <div className="relative">
                <Briefcase
                  className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
                  style={{ color: cssVars.neutral.textMuted }}
                />
                <select
                  value={primaryCategoryId}
                  onChange={(e) => setPrimaryCategoryId(e.target.value)}
                  required
                  className="w-full appearance-none rounded-lg border-2 py-2.5 pl-3 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-12 sm:text-base"
                  style={{
                    backgroundColor: cssVars.neutral.bg,
                    color: primaryCategoryId ? cssVars.secondary.DEFAULT : cssVars.neutral.textMuted,
                    borderColor: cssVars.neutral.border,
                  }}
                >
                  <option value="" disabled>
                    {t('register.specializationPlaceholder')}
                  </option>
                  {categories
                    ?.filter((cat) => !cat.parentId && cat.isActive)
                    ?.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {locale === 'ar' ? cat.nameAr || cat.nameEn : cat.nameEn || cat.nameAr}
                      </option>
                    ))}
                </select>
              </div>
              <p className="mt-1 text-xs" style={{ color: cssVars.neutral.textSecondary }}>
                {t('register.specializationHint')}
              </p>
            </div>
          </>
        )}

        {/* Password Fields */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
              style={{ color: cssVars.secondary.DEFAULT }}
            >
              {t('common.password')}
            </label>
            <div className="relative">
              <Lock
                className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
                style={{ color: cssVars.neutral.textMuted }}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full rounded-lg border-2 py-2.5 pl-10 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-12 sm:pr-12 sm:text-base"
                style={{
                  backgroundColor: cssVars.neutral.bg,
                  color: cssVars.secondary.DEFAULT,
                  borderColor: cssVars.neutral.border,
                }}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-3 top-1/2 -translate-y-1/2 transition-all sm:left-4"
              >
                {showPassword ? (
                  <EyeOff
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    style={{ color: cssVars.neutral.textMuted }}
                  />
                ) : (
                  <Eye
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    style={{ color: cssVars.neutral.textMuted }}
                  />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
              style={{ color: cssVars.secondary.DEFAULT }}
            >
              {t('common.confirmPassword')}
            </label>
            <div className="relative">
              <Lock
                className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 sm:right-4 sm:h-5 sm:w-5"
                style={{ color: cssVars.neutral.textMuted }}
              />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full rounded-lg border-2 py-2.5 pl-10 pr-10 text-sm font-semibold outline-none transition-all focus:border-opacity-100 sm:rounded-xl sm:py-3 sm:pl-12 sm:pr-12 sm:text-base"
                style={{
                  backgroundColor: cssVars.neutral.bg,
                  color: cssVars.secondary.DEFAULT,
                  borderColor: cssVars.neutral.border,
                }}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute left-3 top-1/2 -translate-y-1/2 transition-all sm:left-4"
              >
                {showConfirmPassword ? (
                  <EyeOff
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    style={{ color: cssVars.neutral.textMuted }}
                  />
                ) : (
                  <Eye
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    style={{ color: cssVars.neutral.textMuted }}
                  />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* OTP Verification Section */}
        <div>
          <label
            className="mb-1.5 block text-xs font-bold sm:mb-2 sm:text-sm"
            style={{ color: cssVars.secondary.DEFAULT }}
          >
            {t('register.otp.label')}
          </label>

          {/* OTP Delivery Method */}
          <div className="mb-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setOtpMethod('phone')}
              className="flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-bold transition-all sm:rounded-xl sm:px-4 sm:py-2.5 sm:text-sm"
              style={{
                backgroundColor:
                  otpMethod === 'phone' ? cssVars.primary.DEFAULT : cssVars.neutral.surface,
                borderColor: otpMethod === 'phone' ? cssVars.primary.DEFAULT : cssVars.neutral.border,
                color: otpMethod === 'phone' ? cssVars.neutral.white : cssVars.secondary.DEFAULT,
              }}
            >
              <Phone className="h-4 w-4" />
              {t('register.otp.methodWhatsApp')}
            </button>
            <button
              type="button"
              onClick={() => setOtpMethod('email')}
              className="flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-bold transition-all sm:rounded-xl sm:px-4 sm:py-2.5 sm:text-sm"
              style={{
                backgroundColor:
                  otpMethod === 'email' ? cssVars.primary.DEFAULT : cssVars.neutral.surface,
                borderColor: otpMethod === 'email' ? cssVars.primary.DEFAULT : cssVars.neutral.border,
                color: otpMethod === 'email' ? cssVars.neutral.white : cssVars.secondary.DEFAULT,
              }}
            >
              <Mail className="h-4 w-4" />
              {t('register.otp.methodEmail')}
            </button>
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={isOtpVerified}
                maxLength={6}
                className="w-full rounded-lg border-2 px-3 py-2.5 text-center text-sm font-semibold tracking-widest outline-none transition-all focus:border-opacity-100 disabled:opacity-50 sm:rounded-xl sm:py-3 sm:text-base"
                style={{
                  backgroundColor: isOtpVerified
                    ? `color-mix(in srgb, ${cssVars.status.success} 10%, ${cssVars.neutral.bg})`
                    : cssVars.neutral.bg,
                  color: cssVars.secondary.DEFAULT,
                  borderColor: isOtpVerified ? cssVars.status.success : cssVars.neutral.border,
                }}
                placeholder={t('register.otp.placeholder')}
              />
              {isOtpVerified && (
                <CheckCircle
                  className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2"
                  style={{ color: cssVars.status.success }}
                />
              )}
            </div>

            {!isOtpVerified && (
              <motion.button
                type="button"
                onClick={isOtpSent && otpCode.length >= 4 ? handleVerifyOtp : handleSendOtp}
                disabled={
                  isSendingOtp ||
                  isVerifyingOtp ||
                  (isOtpSent && otpCooldown > 0 && otpCode.length < 4)
                }
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-xs font-bold transition-all disabled:opacity-50 sm:rounded-xl sm:px-6 sm:py-3 sm:text-sm"
                style={{
                  backgroundColor: cssVars.primary.DEFAULT,
                  borderColor: cssVars.primary.DEFAULT,
                  color: cssVars.neutral.white,
                }}
              >
                {isSendingOtp || isVerifyingOtp ? (
                  <LoadingSpinner size="sm" />
                ) : isOtpSent && otpCode.length >= 4 ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    {t('register.otp.verify')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    {isOtpSent && otpCooldown > 0 ? `${otpCooldown}s` : t('register.otp.sendCode')}
                  </>
                )}
              </motion.button>
            )}
          </div>

          {/* OTP Status Message */}
          {isOtpSent && !isOtpVerified && (
            <p className="mt-2 text-xs" style={{ color: cssVars.neutral.textSecondary }}>
              {t(otpMethod === 'email' ? 'register.otp.sentToEmail' : 'register.otp.sentToWhatsApp')}
            </p>
          )}
          {isOtpVerified && (
            <p className="mt-2 text-xs font-medium" style={{ color: cssVars.status.success }}>
              {t(
                otpMethod === 'email'
                  ? 'register.otp.verifiedEmailSuccess'
                  : 'register.otp.verifiedPhoneSuccess'
              )}
            </p>
          )}
        </div>

        {/* Terms Agreement */}
        <label className="flex cursor-pointer items-start gap-2 sm:gap-3">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
            required
            className="mt-0.5 h-4 w-4 cursor-pointer rounded border-2 sm:mt-1 sm:h-5 sm:w-5"
            style={{ accentColor: cssVars.primary.DEFAULT }}
          />
          <span className="text-xs sm:text-sm" style={{ color: cssVars.neutral.textSecondary }}>
            {t.rich('register.termsAgreement', {
              terms: (chunks) => (
                <Link
                  href="/terms"
                  className="font-bold hover:underline"
                  style={{ color: cssVars.primary.DEFAULT }}
                >
                  {chunks}
                </Link>
              ),
              privacy: (chunks) => (
                <Link
                  href="/privacy"
                  className="font-bold hover:underline"
                  style={{ color: cssVars.primary.DEFAULT }}
                >
                  {chunks}
                </Link>
              ),
            })}
          </span>
        </label>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || !isOtpVerified}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-base font-bold shadow-lg transition-all disabled:opacity-50 sm:gap-3 sm:rounded-xl sm:py-4 sm:text-lg"
          style={{
            background: isOtpVerified ? cssVars.gradient.gold : cssVars.neutral.border,
            color: isOtpVerified ? cssVars.brand.secondary : cssVars.neutral.textMuted,
          }}
        >
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              {t('register.submitButton')}
              {isRTL ? (
                <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </>
          )}
        </button>
      </form>

      {/* Sign In Link */}
      <div className="mt-6 text-center sm:mt-8">
        <p className="text-xs sm:text-sm" style={{ color: cssVars.neutral.textSecondary }}>
          {t('common.alreadyHaveAccount')}{' '}
          <Link
            href={`/${locale}/login`}
            className="font-bold transition-all hover:underline"
            style={{ color: cssVars.primary.DEFAULT }}
          >
            {t('register.signInLink')}
          </Link>
        </p>
      </div>
    </Card>
  );
}
